import React, { useEffect, useState, useRef } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';

interface DeviceOption {
  deviceId: string;
  label: string;
}

interface CustomDeviceDropdownProps {
  options: DeviceOption[];
  value: string;
  onSelect: (deviceId: string) => void;
  placeholder: string;
  label: string;
}

export function CustomDeviceDropdown({
  options,
  value,
  onSelect,
  placeholder,
  label
}: CustomDeviceDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.deviceId === value);
  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative" ref={dropdownRef}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full flex items-center justify-between p-3 border rounded-lg
            text-sm transition-colors duration-200 cursor-pointer
            ${isOpen ? 'ring-2 ring-[#1D31FF] border-transparent' : 'border-gray-200'}
            hover:bg-gray-50
          `}
        >
          <div className="flex items-center">
            {selectedOption ? (
              <span className="text-gray-900">{selectedOption.label}</span>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <ChevronDown 
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 
              ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search devices..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-[#1D31FF] focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-60 overflow-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.deviceId}
                    type="button"
                    onClick={() => {
                      onSelect(option.deviceId);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    className={`
                      w-full px-4 py-3 text-left text-sm
                      flex items-center justify-between
                      hover:bg-gray-50 transition-colors
                      ${option.deviceId === value ? 'text-[#1D31FF] font-medium' : 'text-gray-700'}
                    `}
                  >
                    <span>{option.label}</span>
                    {option.deviceId === value && (
                      <Check className="h-4 w-4 text-[#1D31FF]" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No devices found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
